class CollectionFilters {
  constructor(container) {
    this.container = container;
    this.layout = container.dataset.layout;
    this.filterContent = container.querySelector('[data-filter-content]');
    this.filterForm = container.querySelector('#collection-filter-form');
    this.sortSelects = container.querySelectorAll('[data-sort-select]');
    this.loadingOverlay = document.getElementById('filter-loading');
    this.isSearchPage = window.location.pathname.includes('/search');

    if (this.isSearchPage) {
      this.collectionUrl = '/search';
    } else {
      this.collectionUrl = this.filterForm ? this.filterForm.dataset.collectionUrl : window.location.pathname;
    }

    this.debounceTimer = null;

    this.init();
  }

  init() {
    this.bindEvents();
    this.initCollapsibles();
    this.updateUI();
  }

  bindEvents() {
    const toggleBtns = this.container.querySelectorAll('[data-filter-toggle]');
    toggleBtns.forEach(btn => {
      btn.addEventListener('click', () => this.toggleFilters());
    });

    const closeBtns = this.container.querySelectorAll('[data-filter-close]');
    closeBtns.forEach(btn => {
      btn.addEventListener('click', () => this.closeFilters());
    });

    const overlay = this.container.querySelector('[data-filter-overlay]');
    if (overlay) {
      overlay.addEventListener('click', () => this.closeFilters());
    }

    if (this.filterContent) {
      this.filterContent.addEventListener('click', (e) => {
        const filterPanel = this.filterContent.querySelector('.filter-panel');
        if (filterPanel && !filterPanel.contains(e.target)) {
          this.closeFilters();
        }
      });
    }

    if (this.sortSelects && this.sortSelects.length > 0) {
      this.sortSelects.forEach(sortSelect => {
        sortSelect.addEventListener('change', (e) => {
          this.handleSortChange(e.target.value);
          this.syncSortDropdowns(e.target.value);
        });
      });
    }

    if (this.filterForm) {
      const checkboxes = this.filterForm.querySelectorAll('input[type="checkbox"]');
      checkboxes.forEach(checkbox => {
        checkbox.addEventListener('change', () => {
          this.updateUI();
          this.handleFilterChange();
        });
      });

      const priceInputs = this.filterForm.querySelectorAll('input[type="number"]');
      priceInputs.forEach(input => {
        input.addEventListener('input', () => {
          this.updateUI();
          clearTimeout(this.debounceTimer);
          this.debounceTimer = setTimeout(() => {
            this.handleFilterChange();
          }, 800);
        });
      });

      this.filterForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleFilterChange();
      });
    }

    const clearAllBtns = this.container.querySelectorAll('[data-clear-all], [data-clear-filters]');
    clearAllBtns.forEach(btn => {
      btn.addEventListener('click', () => this.clearAllFilters());
    });



    const applyBtn = this.container.querySelector('.btn-apply');
    if (applyBtn) {
      applyBtn.addEventListener('click', () => {
        this.closeFilters();
      });
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.filterContent && this.filterContent.classList.contains('active')) {
        this.closeFilters();
      }
    });

    this.container.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-remove-filter]');
      if (btn) {
        e.preventDefault();
        const param = btn.dataset.filterParam;
        const value = btn.dataset.filterValue;
        this.removeFilter(param, value);
      }
    });
  }

  syncSortDropdowns(value) {
    if (this.sortSelects && this.sortSelects.length > 1) {
      this.sortSelects.forEach(select => {
        if (select.value !== value) {
          select.value = value;
        }
      });
    }
  }

  initCollapsibles() {
    const triggers = this.container.querySelectorAll('[data-collapsible-trigger]');
    triggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const section = trigger.closest('.filter-section');
        section.classList.toggle('active');
      });
    });

    if (this.layout !== 'horizontal') {
      const sections = this.container.querySelectorAll('.filter-section');
      sections.forEach((section, index) => {
        if (section.classList.contains('sort-section') || index < 4) {
          section.classList.add('active');
        }
      });
    }
  }

  async handleSortChange(sortValue) {
    const url = new URL(window.location);

    if (sortValue && sortValue !== 'manual') {
      url.searchParams.set('sort_by', sortValue);
    } else {
      url.searchParams.delete('sort_by');
    }

    url.searchParams.delete('page');

    await this.updateCollection(url);
  }

  async handleFilterChange() {
    if (!this.filterForm) return;

    const url = this.buildFilterURL();
    await this.updateCollection(url);
  }

  buildFilterURL() {
    const url = new URL(window.location.origin + this.collectionUrl);
    const formData = new FormData(this.filterForm);
    const currentUrl = new URL(window.location);
    const sortBy = currentUrl.searchParams.get('sort_by');
    if (sortBy) {
      url.searchParams.set('sort_by', sortBy);
    }

    if (this.isSearchPage) {
      const searchQuery = currentUrl.searchParams.get('q');
      const searchType = currentUrl.searchParams.get('type');

      if (searchQuery) {
        url.searchParams.set('q', searchQuery);
      }
      if (searchType) {
        url.searchParams.set('type', searchType);
      }
    }

    const filterParams = {};

    for (const [key, value] of formData.entries()) {
      if (value && value.trim() !== '') {
        url.searchParams.append(key, value);
      }
    }

    Object.entries(filterParams).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });

    return url;
  }

  async removeFilter(param, value) {
    const url = new URL(window.location);

    if (this.filterForm) {
      const checkbox = this.filterForm.querySelector(`input[name="${param}"][value="${value}"]`);
      if (checkbox) {
        checkbox.checked = false;
      }
    }

    const currentValues = url.searchParams.get(param);

    if (currentValues) {
      const values = currentValues.split(',');
      url.searchParams.delete(param);

      const currentInputs = this.filterForm.querySelectorAll(
        `input[name="${param}"]:checked`
      );

      currentInputs.forEach(input => {
        url.searchParams.append(param, input.value);
      });
    }

    await this.updateCollection(url);
  }

  async clearAllFilters() {
    const url = new URL(window.location.origin + this.collectionUrl);
    const currentUrl = new URL(window.location);

    const sortBy = currentUrl.searchParams.get('sort_by');
    if (sortBy && sortBy !== 'manual') {
      url.searchParams.set('sort_by', sortBy);
    }

    if (this.isSearchPage) {
      const searchQuery = currentUrl.searchParams.get('q');
      const searchType = currentUrl.searchParams.get('type');

      if (searchQuery) {
        url.searchParams.set('q', searchQuery);
      }
      if (searchType) {
        url.searchParams.set('type', searchType);
      }
    }

    if (this.filterForm) {
      const checkboxes = this.filterForm.querySelectorAll('input[type="checkbox"]');
      checkboxes.forEach(checkbox => {
        checkbox.checked = false;
      });

      const priceInputs = this.filterForm.querySelectorAll('input[type="number"]');
      priceInputs.forEach(input => {
        input.value = '';
      });
    }

    await this.updateCollection(url);
  }

  async updateCollection(url) {
    try {
      if (this.loadingOverlay) {
        this.loadingOverlay.classList.add('active');
      }

      const sectionId = this.isSearchPage ? 'main-search' : (this.filterForm ? this.filterForm.dataset.sectionId : 'main-collection');
      const fetchUrl = new URL(url);
      fetchUrl.searchParams.set('section_id', sectionId);

      const response = await fetch(fetchUrl.toString());
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newSection = doc.querySelector(`#${sectionId}`);
      const currentSection = document.querySelector(`#${sectionId}`);
      const wasDrawerOpen = this.filterContent && this.filterContent.classList.contains('active');
      const scrollPosition = this.filterContent ? this.filterContent.querySelector('.filter-scroll-content')?.scrollTop || 0 : 0;

      if (newSection && currentSection) {
        const newProductWrap = newSection.querySelector('.main-collection-product-wrap, .main-collection-wrap');
        const currentProductWrap = currentSection.querySelector('.main-collection-product-wrap, .main-collection-wrap');

        if (newProductWrap && currentProductWrap) {
          currentProductWrap.innerHTML = newProductWrap.innerHTML;

          if (window.reinitAllAnimations && typeof window.reinitAllAnimations === 'function') {
            window.reinitAllAnimations();
          } else {
            if (window.initSectionAnimations && typeof window.initSectionAnimations === 'function') {
              const sectionEl = document.querySelector(`#${sectionId}`) || document;
              window.initSectionAnimations(sectionEl);
            }
            if (window.ScrollTrigger && typeof window.ScrollTrigger.refresh === 'function') {
              window.ScrollTrigger.refresh();
            }
          }
        }

        const newFilterContainer = newSection.querySelector('.collection-filters');
        if (newFilterContainer) {
          this.updateFilterUI(newFilterContainer);
        }

      } else {
        const newProductWrap = doc.querySelector('.main-collection-product-wrap, .main-collection-wrap');
        const currentProductWrap = document.querySelector('.main-collection-product-wrap, .main-collection-wrap');

        if (newProductWrap && currentProductWrap) {
          currentProductWrap.innerHTML = newProductWrap.innerHTML;

          if (window.reinitAllAnimations && typeof window.reinitAllAnimations === 'function') {
            window.reinitAllAnimations();
          } else {
            if (window.initSectionAnimations && typeof window.initSectionAnimations === 'function') {
              const sectionEl = document.querySelector(`#${sectionId}`) || document;
              window.initSectionAnimations(sectionEl);
            }
            if (window.ScrollTrigger && typeof window.ScrollTrigger.refresh === 'function') {
              window.ScrollTrigger.refresh();
            }
          }
        }

        const newFilters = doc.querySelector('.collection-filters');
        if (newFilters) {
          this.updateFilterUI(newFilters);
        }
      }

      window.history.pushState({}, '', url.toString());

      const newTitle = doc.querySelector('title');
      if (newTitle) {
        document.title = newTitle.textContent;
      }

      if (wasDrawerOpen && this.filterContent) {
        this.filterContent.classList.add('active');
        if (this.layout === 'drawer' || window.innerWidth <= 992) {
          document.body.classList.add('no-scroll');
        }

        setTimeout(() => {
          const scrollContent = this.filterContent.querySelector('.filter-scroll-content');
          if (scrollContent) {
            scrollContent.scrollTop = scrollPosition;
          }
        }, 0);
      }

    } catch (error) {

      window.location.href = url.toString();
    } finally {
      if (this.loadingOverlay) {
        this.loadingOverlay.classList.remove('active');
      }
    }
  }

  updateFilterUI(newFilterContainer) {
    const newActiveFiltersSummary = newFilterContainer.querySelector('.active-filters-summary');
    const currentActiveFiltersSummary = this.container.querySelector('.active-filters-summary');

    if (newActiveFiltersSummary && currentActiveFiltersSummary) {
      currentActiveFiltersSummary.innerHTML = newActiveFiltersSummary.innerHTML;


      const clearAllBtn = currentActiveFiltersSummary.querySelector('[data-clear-all]');
      if (clearAllBtn) {
        clearAllBtn.addEventListener('click', () => this.clearAllFilters());
      }
    } else if (newActiveFiltersSummary && !currentActiveFiltersSummary) {
      const scrollContent = this.container.querySelector('.filter-scroll-content');
      if (scrollContent) {
        const sortSection = scrollContent.querySelector('.sort-section');
        if (sortSection) {
          sortSection.insertAdjacentHTML('afterend', newActiveFiltersSummary.outerHTML);
        } else {
          scrollContent.insertAdjacentHTML('afterbegin', newActiveFiltersSummary.outerHTML);
        }

        const summary = scrollContent.querySelector('.active-filters-summary');
        if (summary) {


          const clearAllBtn = summary.querySelector('[data-clear-all]');
          if (clearAllBtn) {
            clearAllBtn.addEventListener('click', () => this.clearAllFilters());
          }
        }
      }
    } else if (!newActiveFiltersSummary && currentActiveFiltersSummary) {
      currentActiveFiltersSummary.remove();
    }

    const newFilterSections = newFilterContainer.querySelectorAll('.filter-section[data-filter-type]');
    newFilterSections.forEach(newSection => {
      const filterType = newSection.dataset.filterType;
      const currentSection = this.container.querySelector(`.filter-section[data-filter-type="${filterType}"]`);

      if (!currentSection) return;

      const newTitle = newSection.querySelector('.filter-title');
      const currentTitle = currentSection.querySelector('.filter-title');

      if (newTitle && currentTitle) {
        const newActiveCount = newTitle.querySelector('.filter-active-count');
        const currentActiveCount = currentTitle.querySelector('.filter-active-count');

        if (newActiveCount && currentActiveCount) {
          currentActiveCount.textContent = newActiveCount.textContent;
        } else if (newActiveCount && !currentActiveCount) {
          const span = document.createElement('span');
          span.className = 'filter-active-count';
          span.textContent = newActiveCount.textContent;
          currentTitle.appendChild(span);
        } else if (!newActiveCount && currentActiveCount) {
          currentActiveCount.remove();
        }
      }

      const newOptions = newSection.querySelectorAll('.filter-option');
      newOptions.forEach(newOption => {
        const input = newOption.querySelector('input');
        if (!input) return;

        const name = input.name;
        const value = input.value;
        const currentInput = currentSection.querySelector(`input[name="${name}"][value="${value}"]`);
        const currentOption = currentInput?.closest('.filter-option');

        if (!currentOption) return;

        const newCount = newOption.querySelector('.filter-count');
        const currentCount = currentOption.querySelector('.filter-count');

        if (newCount && currentCount) {
          currentCount.textContent = newCount.textContent;
        }
      });
    });

    this.updateUI();
  }

  toggleFilters() {
    if (this.layout === 'drawer' || window.innerWidth <= 992) {
      this.filterContent.classList.toggle('active');
      if (this.filterContent.classList.contains('active')) {
        document.body.classList.add('no-scroll');
      } else {
        document.body.classList.remove('no-scroll');
      }
    }
  }

  closeFilters() {
    if (this.filterContent) {
      this.filterContent.classList.remove('active');
    }
    document.body.classList.remove('no-scroll');
  }

  updateUI() {
    let activeCount = 0;

    if (this.filterForm) {
      const checkedBoxes = this.filterForm.querySelectorAll('input[type="checkbox"]:checked');
      activeCount += checkedBoxes.length;

      const priceInputs = this.filterForm.querySelectorAll('input[type="number"]');
      priceInputs.forEach(input => {
        if (input.value && input.value.trim() !== '') activeCount++;
      });
    } else {
      const url = new URL(window.location);
      for (const [key, value] of url.searchParams.entries()) {
        if (
          key !== 'sort_by' &&
          key !== 'page' &&
          key !== 'section_id' &&
          key !== 'q' &&
          key !== 'type' &&
          value
        ) {
          activeCount += value.split(',').length;
        }
      }
    }

    const counters = this.container.querySelectorAll('.filter-count[data-active-count]');
    counters.forEach(counter => {
      counter.setAttribute('data-active-count', activeCount);
      counter.textContent = activeCount;
    });

    const clearBtns = this.container.querySelectorAll('[data-clear-btn]');
    clearBtns.forEach(btn => {
      btn.style.display = activeCount > 0 ? '' : 'none';
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const filterContainers = document.querySelectorAll('.collection-filters');
  filterContainers.forEach(container => {
    new CollectionFilters(container);
  });
});

window.addEventListener('popstate', () => {
  location.reload();
});

document.addEventListener('shopify:section:load', () => {
  const filterContainers = document.querySelectorAll('.collection-filters');
  filterContainers.forEach(container => {
    new CollectionFilters(container);
  });
});

function reinitAnimations() {
  if (window.reinitAllAnimations) {
    window.reinitAllAnimations();
  } else {
    document.querySelectorAll("[data-card-animate], [data-saos]").forEach(el => {
      el.classList.remove("is-animated");
      el.style.opacity = "";
      el.style.transform = "";
    });
  }
}